
'use client';
import React, { useState } from "react";
import * as Yup from 'yup';
import AuthLayout from "./AuthLayout";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { useResetPasswordMutation } from "../../services/auth";
import { useFormik } from "formik";
import { showError, showToast } from "../../helpers";
import { USE_FAKE_DATA } from "../../config/appConfig";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const formik = useFormik({
    initialValues: {
      newpassword: '',
      confirmpassword: '',
    },
    validationSchema: Yup.object({
      newpassword: Yup.string()
        .label('new Password')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/,
          'Password must be at least 8 characters long and include 1 uppercase, 1 lowercase, and 1 special character.',
        )
        .required('New password is required.'),

      confirmpassword: Yup.string()
        .oneOf([Yup.ref('newpassword')], 'Passwords must match.')
        .required('Confirm password is required.'),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);
      const body = {
        newPassword: formik.values.newpassword,
      };

      try {
        if (USE_FAKE_DATA) {
          navigate('/');
          showToast('Password reset successfully');
          return;
        }
        const response = await resetPassword(body as any).unwrap();
        if (response?.statusCode === 200) {
          navigate('/');
          showToast('Password reset successfully');
        }
      } catch (error: any) {
        showError(error?.data?.message || '');
      }
      formik.setSubmitting(false);
    },
  });

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Reset Password</h2>
        <p>Create a secure password to protect your account.</p>
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label htmlFor="new-password">New Password</label>
             <TextField
            sx={{ m: 0 }}
            hiddenLabel
            placeholder="Password"
            fullWidth
            margin="normal"
            name="newpassword"
            className="text_field"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.newpassword && formik.errors.newpassword}
            InputProps={{
              endAdornment: (
                <InputAdornment className="eye_btn" position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowPassword((prevShowPassword) => !prevShowPassword);
                    }}
                    onMouseDown={(e: any) => {
                      e.preventDefault();
                    }}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </div>

          <div className="control_group">
            <label htmlFor="confirm-password">Confirm Password</label>
             <TextField
            sx={{ m: 0 }}
            hiddenLabel
            placeholder="Confirm Password"
            fullWidth
            className="text_field"
            margin="normal"
            name="confirmpassword"
            type={showConfirmPassword ? 'text' : 'password'}
            variant="outlined"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.confirmpassword && formik.errors.confirmpassword}
            InputProps={{
              endAdornment: (
                <InputAdornment className="eye_btn" position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
                    }}
                    onMouseDown={(e: any) => {
                      e.preventDefault();
                    }}
                    edge="end"
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary w_100"
              type="submit"
              disabled={USE_FAKE_DATA ? false : isLoading}
            >
              Send
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
